import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  useBreakpointValue,
  Spinner,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  HStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  IconButton,
  Badge,
  Divider,
} from '@chakra-ui/react'
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi'
import { getAuctionCaseList, getAuctionCaseDetail } from '../services/apiService'
import dayjs from 'dayjs'

interface AuctionCaseItem {
  id: number
  title: string
  content: string
  images: string[] | null
  viewCount: number
  createdAt: string
  updatedAt?: string | null
}

const AuctionCases: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [auctionCases, setAuctionCases] = useState<AuctionCaseItem[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedItem, setSelectedItem] = useState<AuctionCaseItem | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)

  const fetchList = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAuctionCaseList(page, 12)
      if (res.statusCode === 200) {
        setAuctionCases(res.data.auctionCases)
        setTotalPages(res.data.totalPages)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDetail = async (id: number) => {
    setDetailLoading(true)
    try {
      const { data: res } = await getAuctionCaseDetail(id)
      if (res.statusCode === 200) {
        setSelectedItem(res.data)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
  }, [page])

  const handleCardClick = (id: number) => {
    fetchDetail(id)
  }

  const handleBack = () => {
    setSelectedItem(null)
  }

  const openImageViewer = (url: string) => {
    setSelectedImage(url)
    setImageViewerOpen(true)
  }

  const closeImageViewer = () => {
    setSelectedImage(null)
    setImageViewerOpen(false)
  }

  // 상세 페이지
  if (selectedItem) {
    return (
      <DefaultLayout>
        <Container maxW="container.lg" py={10}>
          <VStack spacing={6} align="stretch">
            <HStack>
              <IconButton
                aria-label="뒤로가기"
                icon={<FiArrowLeft />}
                variant="ghost"
                onClick={handleBack}
              />
              <Heading as="h1" size="lg">
                {selectedItem.title}
              </Heading>
            </HStack>

            <HStack spacing={4} color="gray.500" fontSize="sm">
              <HStack>
                <FiEye />
                <Text>{selectedItem.viewCount}</Text>
              </HStack>
              <Text>|</Text>
              <Text>{dayjs(selectedItem.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
            </HStack>

            <Divider />

            {/* 이미지 갤러리 */}
            {selectedItem.images && selectedItem.images.length > 0 && (
              <Box>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {selectedItem.images.map((url, idx) => (
                    <Box
                      key={idx}
                      cursor="pointer"
                      onClick={() => openImageViewer(url)}
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="md"
                      _hover={{ transform: 'scale(1.02)', boxShadow: 'lg' }}
                      transition="all 0.2s"
                    >
                      <Image
                        src={url}
                        alt={`이미지 ${idx + 1}`}
                        w="100%"
                        h="200px"
                        objectFit="cover"
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* 내용 */}
            <Box
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
              whiteSpace="pre-wrap"
              lineHeight="tall"
            >
              <Text>{selectedItem.content}</Text>
            </Box>

            <Button onClick={handleBack} variant="outline" alignSelf="center">
              목록으로
            </Button>
          </VStack>
        </Container>

        {/* 이미지 뷰어 모달 */}
        <Modal isOpen={imageViewerOpen} onClose={closeImageViewer} size="full" isCentered>
          <ModalOverlay bg="blackAlpha.900" />
          <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
            <ModalCloseButton color="white" size="lg" />
            <ModalBody
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={0}
            >
              {selectedImage && (
                <Image
                  src={selectedImage}
                  alt="확대 이미지"
                  maxW="90vw"
                  maxH="90vh"
                  objectFit="contain"
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </DefaultLayout>
    )
  }

  // 목록 페이지
  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading
              as="h1"
              size={isMobile ? 'xl' : '2xl'}
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
              fontWeight="bold"
              mb={4}
            >
              {t('경매 사례')}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {t('성공적인 경매 사례를 확인하세요')}
            </Text>
          </Box>

          {loading ? (
            <Box textAlign="center" py={20}>
              <Spinner size="xl" />
            </Box>
          ) : auctionCases.length === 0 ? (
            <Box textAlign="center" py={20}>
              <Text color="gray.500">등록된 경매사례가 없습니다.</Text>
            </Box>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {auctionCases.map((item) => (
                  <Card
                    key={item.id}
                    cursor="pointer"
                    onClick={() => handleCardClick(item.id)}
                    _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }}
                    transition="all 0.2s"
                    overflow="hidden"
                  >
                    {item.images && item.images.length > 0 && (
                      <Image
                        src={item.images[0]}
                        alt={item.title}
                        h="180px"
                        w="100%"
                        objectFit="cover"
                      />
                    )}
                    <CardBody>
                      <VStack align="stretch" spacing={3}>
                        <Heading size="md" noOfLines={2}>
                          {item.title}
                        </Heading>
                        <Text color="gray.600" fontSize="sm" noOfLines={3}>
                          {item.content}
                        </Text>
                        <HStack justify="space-between" fontSize="xs" color="gray.500">
                          <HStack>
                            <FiEye />
                            <Text>{item.viewCount}</Text>
                          </HStack>
                          <Text>{dayjs(item.createdAt).format('YYYY-MM-DD')}</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <HStack justify="center" spacing={4} pt={4}>
                  <IconButton
                    aria-label="이전"
                    icon={<FiChevronLeft />}
                    isDisabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  />
                  <Text>
                    {page} / {totalPages}
                  </Text>
                  <IconButton
                    aria-label="다음"
                    icon={<FiChevronRight />}
                    isDisabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  />
                </HStack>
              )}
            </>
          )}
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default AuctionCases
